import { Link } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import {
  pointRules,
  pointTableSections,
  levelExperienceData,
} from "@/mocks/levelGuideData";

function PointTable({ section }: { section: (typeof pointTableSections)[0] }) {
  return (
    <div className="mb-6">
      <h4 className="text-base font-bold text-foreground-800 mb-3 pl-3 border-l-4 border-primary-500">
        {section.title}
      </h4>
      <div className="overflow-x-auto rounded-lg border border-background-300">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-primary-500 text-foreground-50">
              <th className="px-4 py-2.5 text-center font-semibold w-[40%]">항목</th>
              <th className="px-4 py-2.5 text-center font-semibold w-[30%]">포인트</th>
              <th className="px-4 py-2.5 text-center font-semibold w-[30%]">비고</th>
            </tr>
          </thead>
          <tbody>
            {section.items.map((item, idx) => (
              <tr
                key={idx}
                className={
                  idx % 2 === 0
                    ? "bg-background-50"
                    : "bg-background-100/70"
                }
              >
                <td className="px-4 py-2.5 text-center text-foreground-700">
                  {item.action}
                </td>
                <td className="px-4 py-2.5 text-center font-semibold text-primary-600">
                  {item.points}
                </td>
                <td className="px-4 py-2.5 text-center text-foreground-600 text-xs">
                  {item.note}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LevelBadge({ level }: { level: number }) {
  const getBadgeColor = (lv: number) => {
    if (lv <= 10) return "bg-secondary-200 text-secondary-800";
    if (lv <= 30) return "bg-accent-100 text-accent-800";
    if (lv <= 50) return "bg-primary-100 text-primary-800";
    if (lv <= 70) return "bg-accent-200 text-accent-900";
    return "bg-primary-200 text-primary-900";
  };

  return (
    <span
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold ${getBadgeColor(level)}`}
    >
      {level}
    </span>
  );
}

function LevelTableColumn({
  data,
}: {
  data: { level: number; exp: string }[];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-background-300">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-primary-500 text-foreground-50">
            <th className="px-3 py-2.5 text-center font-semibold">레벨</th>
            <th className="px-3 py-2.5 text-center font-semibold">필요경험치</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, idx) => (
            <tr
              key={item.level}
              className={
                idx % 2 === 0
                  ? "bg-background-50"
                  : "bg-background-100/70"
              }
            >
              <td className="px-3 py-2 text-center">
                <div className="flex items-center justify-center">
                  <LevelBadge level={item.level} />
                </div>
              </td>
              <td className="px-3 py-2 text-center">
                <span className="inline-block px-3 py-1 rounded-md bg-accent-100 text-accent-800 font-semibold text-xs">
                  {item.exp}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LevelGuidePage() {
  const leftLevels = levelExperienceData.slice(0, 48);
  const rightLevels = levelExperienceData.slice(48, 96);

  const relatedGuides = [
    { label: "스포츠중계 이용가이드", href: "/guide" },
    { label: "공지사항", href: "/notices" },
    { label: "자주 묻는 질문 (FAQ)", href: "/faq" },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Page Header */}
      <section className="bg-background-100 border-b border-background-300">
        <div className="max-w-[1000px] mx-auto px-3 md:px-4 py-6 md:py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-400 transition-colors mb-3"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            홈으로
          </Link>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center text-primary-500">
              <i className="ri-vip-crown-line text-2xl" />
            </span>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-700">포인트및경험치</h1>
              <p className="text-sm text-foreground-600 mt-0.5">
                월드컵티비 포인트 적립 제도 및 레벨별 경험치 안내
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-[1000px] mx-auto px-3 md:px-4 py-6 md:py-8">
        {/* Title */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-800 mb-4">
            계급 목록 및 경험치 기준
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            월드컵티비의 포인트 적립 제도와 레벨별 필요 경험치를 한눈에 확인하세요.
            <br />
            꾸준한 활동으로 포인트를 쌓고 더 높은 레벨에 도전해보세요!
          </p>
        </div>

        {/* Point Rules Section */}
        <section className="mb-10">
          <h3 className="text-lg md:text-xl font-bold text-foreground-800 mb-5 pb-3 border-b-2 border-primary-500">
            ({pointRules.title})
          </h3>
          <div className="bg-background-100 border border-background-300 rounded-lg p-5 md:p-6 mb-6">
            <ol className="space-y-3">
              {pointRules.notices.map((notice, idx) => (
                <li
                  key={idx}
                  className="flex gap-3 text-sm text-foreground-700"
                >
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary-500 text-foreground-50 rounded-full text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="leading-relaxed pt-0.5">{notice}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Point Tables */}
          <div className="space-y-2">
            {pointTableSections.map((section) => (
              <PointTable key={section.title} section={section} />
            ))}
          </div>
        </section>

        {/* Level & Experience Section */}
        <section className="mb-10">
          <h3 className="text-lg md:text-xl font-bold text-foreground-800 mb-5 pb-3 border-b-2 border-primary-500">
            레벨별 계급도 및 필요경험치 안내
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <LevelTableColumn data={leftLevels} />
            <LevelTableColumn data={rightLevels} />
          </div>
        </section>

        {/* Point Tips Box */}
        <div className="mb-10">
          <div className="p-4 rounded-lg bg-accent-100/60 border border-accent-300/40">
            <h4 className="text-sm font-bold text-accent-800 mb-2 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                <i className="ri-lightbulb-line" />
              </span>
              포인트 적립 팁
            </h4>
            <ul className="space-y-2 text-sm text-accent-900">
              <li className="flex gap-2">
                <span className="text-accent-600 font-bold">01</span>
                <span>매일 로그인으로 출석 포인트를 받아보세요.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent-600 font-bold">02</span>
                <span>커뮤니티 활동으로 추가 포인트를 적립하세요.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent-600 font-bold">03</span>
                <span>지인 추천 시 1,000P를 추가로 받을 수 있습니다.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent-600 font-bold">04</span>
                <span>적립된 포인트는 보증업체 전환이 가능합니다.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Related Guides */}
        <div className="mb-8">
          <h3 className="text-lg md:text-xl font-bold text-foreground-800 mb-4 pb-3 border-b-2 border-primary-500">
            함께 보면 좋은 가이드
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {relatedGuides.map((guide) => (
              <Link
                key={guide.label}
                to={guide.href}
                className="flex items-center gap-2 p-4 rounded-lg bg-background-100 border border-background-300 hover:border-primary-500/30 hover:bg-background-200/50 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center text-primary-400">
                  <i className="ri-arrow-right-s-fill" />
                </span>
                <span className="text-sm font-medium text-primary-500">{guide.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}